import React, {useCallback, useEffect, useState} from 'react';
import {Button, Card, Form, Input, message, Modal, Select, Space, Table, Tag} from 'antd';
// antd v5 不再从主包导出图标，图标统一从 @/icons 引入
import {useAppStore} from '@/page/app/context';
import AppContent from '@/page/component/AppLayout/AppContent';
import {approvePush, getApprovalDetail, getApprovals, rejectPush} from '@/api/hrPush';
import {formatApiTime} from '../utils';

export default function ApprovalPage() {
    const [{appId}] = useAppStore();
    const [form] = Form.useForm();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [detail, setDetail] = useState(null);
    const [opinion, setOpinion] = useState('');
    const [acting, setActing] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const {data} = await getApprovals(appId, form.getFieldsValue());
            setRows(data?.records || []);
        } finally {
            setLoading(false);
        }
    }, [appId, form]);

    useEffect(() => {
        if (appId) load();
    }, [appId, load]);

    const openDetail = async taskId => {
        const {data} = await getApprovalDetail(appId, taskId);
        setDetail(data);
        setOpinion(data.task?.approvalOpinion || '');
    };

    const act = async type => {
        if (acting) return;
        if (!opinion.trim()) {
            message.warning('审批意见必填');
            return;
        }
        setActing(true);
        try {
            if (type === 'approve') {
                const json = await approvePush(appId, detail.task.id, opinion);
                // 审批已通过但消息发送失败时后端返回 warning，需要明确告知审批人
                const warning = json?.data?.warning;
                if (warning) message.warning(warning, 8);
                else message.success('已通过并发送');
            } else {
                await rejectPush(appId, detail.task.id, opinion);
                message.success('已驳回');
            }
            setDetail(null);
            setOpinion('');
            load();
        } finally {
            setActing(false);
        }
    };

    const columns = [
        {title: '审批单号', dataIndex: 'approvalNo'},
        {title: '通知人员', dataIndex: 'targetNames'},
        {
            title: '申请时间',
            dataIndex: 'createTime',
            width: 170,
            render: formatApiTime,
        },
        {title: '申请人', dataIndex: 'creatorName', width: 120},
        {
            title: '状态',
            dataIndex: 'approvalStatus',
            width: 110,
            render: value => (
                <Tag color={value === 0 ? 'orange' : value === 1 ? 'green' : 'red'}>
                    {value === 0 ? '待审批' : value === 1 ? '已完成' : '已驳回'}
                </Tag>
            ),
        },
        {
            title: '操作',
            width: 90,
            render: (_, row) => (
                <Button type="link" onClick={() => openDetail(row.id)}>
                    {row.approvalStatus === 0 ? '审批' : '查看'}
                </Button>
            ),
        },
    ];

    return (
        <AppContent>
            <Card title="离职消息审批">
                <Form form={form} layout="inline" onFinish={load} style={{marginBottom: 16}}>
                    <Form.Item name="status">
                        <Select
                            placeholder="状态"
                            allowClear
                            style={{width: 120}}
                            options={[
                                {value: 0, label: '待审批'},
                                {value: 1, label: '已完成'},
                                {value: 2, label: '已驳回'},
                            ]}
                        />
                    </Form.Item>
                    <Space>
                        <Button type="primary" htmlType="submit">
                            搜索
                        </Button>
                        <Button
                            onClick={() => {
                                form.resetFields();
                                load();
                            }}
                        >
                            重置
                        </Button>
                    </Space>
                </Form>
                <Table
                    rowKey="id"
                    columns={columns}
                    dataSource={rows}
                    loading={loading}
                    pagination={{pageSize: 10, showTotal: total => `共 ${total} 条记录`}}
                />
                <Modal
                    open={!!detail}
                    title="离职消息审批详情"
                    width={760}
                    onCancel={() => {
                        setDetail(null);
                        setOpinion('');
                    }}
                    footer={
                        detail?.task?.approvalStatus === 0 ? (
                            <Space>
                                <Button disabled={acting} onClick={() => act('reject')}>
                                    驳回
                                </Button>
                                <Button
                                    type="primary"
                                    loading={acting}
                                    onClick={() => act('approve')}
                                >
                                    通过
                                </Button>
                            </Space>
                        ) : null
                    }
                >
                    <Space direction="vertical" style={{width: '100%'}} size={16}>
                        <div>
                            <b>审批单号：</b>
                            {detail?.task?.approvalNo}
                        </div>
                        <Table
                            rowKey="id"
                            size="small"
                            title={() => '离职人员通知清单'}
                            columns={[
                                {title: '序号', render: (_, __, index) => index + 1, width: 70},
                                {
                                    title: '姓名',
                                    dataIndex: 'userName',
                                    render: value => value || '-',
                                },
                                {title: '员工号', dataIndex: 'employeeNumber'},
                                {
                                    title: '职务',
                                    dataIndex: 'position',
                                    render: value => value || '-',
                                },
                            ]}
                            dataSource={detail?.targets || []}
                            pagination={false}
                        />
                        <Input.TextArea
                            rows={4}
                            value={opinion}
                            onChange={event => setOpinion(event.target.value)}
                            placeholder={
                                detail?.task?.approvalOpinion || '请输入审批意见（必填）...'
                            }
                            readOnly={detail?.task?.approvalStatus !== 0}
                        />
                    </Space>
                </Modal>
            </Card>
        </AppContent>
    );
}
